import { useDeferredValue, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Users, Plus, Search } from 'lucide-react';
import { toast } from '@ooh/ui/components/sonner';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';
import { PermissionDeniedState } from '@ooh/ui/components/permission-denied-state';
import { clients } from '@ooh/contracts';
import type { Client } from '@ooh/contracts';
import { useApiSuspenseQuery } from '../data/use-api-suspense-query';
import { PageLayout } from '../layouts/page-layout';
import { useArchiveClient } from './client-mutations';
import { useClientPermissions } from './use-client-permissions';
import { ClientDialog } from './client-dialog';
import { ClientArchiveConfirm } from './client-archive-confirm';
import { ClientsEmptyState, ClientsTable } from './clients-table';

export interface ClientsSearch {
  q?: string;
}

interface ClientsListPageProps {
  search: ClientsSearch;
  onSearchChange: (patch: Partial<ClientsSearch>) => void;
}

type DialogState = { mode: 'create' } | { mode: 'edit'; client: Client };

interface ClientsListContentProps extends ClientsListPageProps {
  activeClientId: Client['id'] | undefined;
  onEdit: (client: Client) => void;
  onArchive: (client: Client) => void;
}

function useArchiveFlow(t: TFunction<'clients'>) {
  const [archiveTarget, setArchiveTarget] = useState<Client | null>(null);
  const archiveMutation = useArchiveClient();

  function confirm() {
    if (archiveTarget === null) return;
    archiveMutation.mutate(archiveTarget.id, {
      onSuccess: () => {
        toast.success(t('actions.archived'));
        setArchiveTarget(null);
      },
    });
  }

  return {
    archiveTarget,
    setArchiveTarget,
    confirmArchive: confirm,
    cancelArchive: () => setArchiveTarget(null),
    isPending: archiveMutation.isPending,
  };
}

function ClientsListContent({
  search,
  onSearchChange,
  activeClientId,
  onEdit,
  onArchive,
}: ClientsListContentProps) {
  const { t } = useTranslation('clients');
  const { canUpdate, canDelete } = useClientPermissions();
  const [selectedClientId, setSelectedClientId] = useState<Client['id'] | null>(null);
  // The list is requested with the deferred search so typing never swaps the page for its skeleton.
  const requested = useDeferredValue(search);
  const { data: rows } = useApiSuspenseQuery(clients.list, { q: requested.q });

  return (
    <>
      <div className="mb-5 flex items-center gap-3">
        <div className="relative flex-1 min-w-48 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder={t('search.placeholder')}
            value={search.q ?? ''}
            onChange={(e) => onSearchChange({ q: e.target.value || undefined })}
            className="pl-9"
          />
        </div>
      </div>
      <div
        data-testid="clients-list"
        className={requested === search ? undefined : 'opacity-60 transition-opacity'}
      >
        {rows.length === 0 ? (
          <ClientsEmptyState t={t} />
        ) : (
          <ClientsTable
            clients={rows}
            canUpdate={canUpdate}
            canDelete={canDelete}
            activeClientId={activeClientId}
            selectedClientId={selectedClientId}
            onEdit={onEdit}
            onArchive={onArchive}
            onSelectClient={(client) => setSelectedClientId(client.id)}
            t={t}
          />
        )}
      </div>
    </>
  );
}

export function ClientsListPage({ search, onSearchChange }: ClientsListPageProps) {
  const { t } = useTranslation('clients');
  const { canView, canCreate } = useClientPermissions();
  const [dialogState, setDialogState] = useState<DialogState | null>(null);
  const { archiveTarget, setArchiveTarget, confirmArchive, cancelArchive, isPending } =
    useArchiveFlow(t);

  if (!canView) {
    return <PermissionDeniedState message={t('permissionDenied')} />;
  }

  const editClientId = dialogState?.mode === 'edit' ? dialogState.client.id : undefined;

  return (
    <>
      <PageLayout
        title={t('pageTitle')}
        description={t('pageSubtitle')}
        icon={<Users className="h-5 w-5" />}
        actions={
          canCreate ? (
            <Button data-testid="createButton" onClick={() => setDialogState({ mode: 'create' })}>
              <Plus className="w-4 h-4" />
              {t('actions.addClient')}
            </Button>
          ) : null
        }
      >
        <ClientsListContent
          search={search}
          onSearchChange={onSearchChange}
          activeClientId={editClientId ?? archiveTarget?.id}
          onEdit={(client) => setDialogState({ mode: 'edit', client })}
          onArchive={setArchiveTarget}
        />
      </PageLayout>

      {dialogState !== null ? (
        <ClientDialog
          mode={dialogState.mode}
          client={dialogState.mode === 'edit' ? dialogState.client : undefined}
          onClose={() => setDialogState(null)}
        />
      ) : null}

      {archiveTarget !== null ? (
        <ClientArchiveConfirm
          client={archiveTarget}
          onCancel={cancelArchive}
          onConfirm={confirmArchive}
          isPending={isPending}
        />
      ) : null}
    </>
  );
}
